/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import {
  HallowAnalyticsEvent,
  useAnalytics,
  useViewedScreen,
} from "@packages/analytics";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { DataTestId, HallowDataTestId } from "@tools/playwright/global";
import { createPortal } from "react-dom";

import type { ThemeType } from "../../../../global/emotion";
import {
  numericPercentages,
  numericViewportHeight,
  numericViewportWidth,
  spacing,
  useTheme,
  zIndices,
} from "../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../lib/constants/emotionMediaQueries.constants";
import { Button } from "../../_base/_core/Button/Button.emotion";
import { IconButton } from "../../_base/_core/IconButton/IconButton.emotion";
import type { TextProps } from "../../_base/_core/Text/Text.emotion";
import { Text } from "../../_base/_core/Text/Text.emotion";
import { HallowLogoPlainText } from "../../_base/icons/HallowLogoText/HallowLogoText.emotion";
import { ArrowLeftIcon } from "../../_base/icons/Icon/NavigationIcons";
import type { StackProps } from "../layout/Stack/Stack.emotion";
import { Stack } from "../layout/Stack/Stack.emotion";
import { AuthLegal } from "./AuthLegal";
import type { AuthFlowProps } from "./AuthTypes.emotion";
import { GoogleAuthButton } from "./GoogleAuthButton.emotion";

const studentImageStyle: Interpolation = {
  backgroundImage:
    "url(https://images.hallow.com/assets/schools.webp), url(https://images.hallow.com/assets/schools_blurred.webp)",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  filter: "brightness(0.7)",
  height: numericViewportHeight[100],
  position: "absolute",
  width: numericViewportWidth[100],
  zIndex: zIndices.behind,
};
const studentContainerStyle: Interpolation<ThemeType> = (theme) => ({
  background: theme.semanticColors.background,
  borderRadius: theme.radius.m,
  paddingInline: theme.spacing.l,
  paddingBottom: theme.spacing.l,
  paddingTop: theme.spacing.xxl,
});
const containerStyle: Interpolation = {
  padding: spacing.ml,
  width: numericPercentages[100],
};
const buttonStyle: Interpolation = {
  position: "absolute",
  top: spacing.xxxl,
  left: spacing.l,
};
const logoStyle: Interpolation = {
  alignSelf: "center",
  height: 24,
  width: 102,
  [cssMediaQueryMinWidthTablet]: { height: 32, width: 141 },
};
const subtitleStyle: Interpolation = {
  alignSelf: "center",
  paddingBlock: spacing.m,
};
const infoContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  paddingInline: spacing.ml,
  textAlign: "center",
};

export type AuthStudentFlowProps = AuthFlowProps & {
  legalCSS?: Interpolation;
  legalProps?: Partial<TextProps>;
} & Pick<StackProps, "className">;

export const AuthStudentFlow = ({
  onAuth,
  setFlow,
  legalCSS = {},
  legalProps,
  allowedMethods,
  className,
}: AuthStudentFlowProps) => {
  const t = useTranslations();
  const { themeContainerRef } = useTheme();
  const { track } = useAnalytics();

  useViewedScreen({ screen: "student_auth_type_login_primer" });

  return (
    <>
      <div css={studentImageStyle} />
      <div css={studentContainerStyle}>
        <div css={infoContainerStyle}>
          <HallowLogoPlainText css={logoStyle} />
          <Text
            size="m"
            type="body"
            css={subtitleStyle}
            data-testid={DataTestId.loginMessage}
          >
            {t("student_login_subtitle")}
          </Text>
        </div>
        <GoogleOAuthProvider
          clientId={process.env.NEXT_PUBLIC_ACCESS_GOOGLE_CLIENT_ID}
        >
          {createPortal(
            <IconButton
              size="s"
              type="button"
              variant="transparentWhite"
              css={buttonStyle}
              icon={<ArrowLeftIcon />}
              onClick={() => setFlow("main")}
            />,
            themeContainerRef.current,
          )}
          <Stack gap={spacing.ms} css={containerStyle} className={className}>
            <Button
              isFullWidth
              size="l"
              onClick={() => {
                track({
                  event: HallowAnalyticsEvent.EmailButtonTapped,
                  properties: { screen_name: "student_auth_type_login_primer" },
                });
                setFlow("usernameOrEmail");
              }}
              data-testid={
                HallowDataTestId.AuthContinueWithEmailOrUsernameButton
              }
            >
              {t("continue_with_email_or_username")}
            </Button>
            {allowedMethods.includes("google") && (
              <GoogleAuthButton
                onAuth={onAuth}
                screen="student_auth_type_login_primer"
              />
            )}
            <AuthLegal legalCSS={legalCSS} legalProps={legalProps} />
          </Stack>
        </GoogleOAuthProvider>
      </div>
    </>
  );
};

AuthStudentFlow.displayName = "AuthStudentFlow";
